import { DEFAULT_WOLF_MULTIPLIERS, mergeWolfConfig } from '@/lib/games/formats/wolf';
import type { WolfMultipliers, WolfRoundConfig } from '@/lib/games/engine/types';

interface Props {
  config: WolfRoundConfig;
  onChange: (config: WolfRoundConfig) => void;
  /** Hide hole point multipliers when game scoring is money-only. */
  showPointMultipliers?: boolean;
}

export function WolfSetupPanel({ config, onChange, showPointMultipliers = true }: Props) {
  const cfg = mergeWolfConfig(config);
  const mult = { ...DEFAULT_WOLF_MULTIPLIERS, ...cfg.multipliers };

  const setMult = (key: keyof WolfMultipliers, value: number) => {
    onChange({ ...cfg, multipliers: { ...mult, [key]: value } });
  };

  return (
    <div className="rounded-xl border border-gold/30 bg-gold/5 p-3.5">
      <div className="mb-2 text-sm font-bold text-navy">Wolf options</div>

      <label className="mb-3 flex items-center gap-2 text-xs text-ink">
        <input
          type="checkbox"
          checked={!!cfg.blindWolfEnabled}
          onChange={(e) => onChange({ ...cfg, blindWolfEnabled: e.target.checked })}
        />
        Enable Blind Wolf (declare before first tee shot)
      </label>

      {cfg.blindWolfEnabled && (
        <div className="mb-3 space-y-2">
          <div className="text-[10px] font-bold uppercase text-mute">Blind partner</div>
          <div className="flex gap-2">
            {(['first-tee', 'organizer'] as const).map((rule) => (
              <button
                key={rule}
                type="button"
                onClick={() => onChange({ ...cfg, blindPartnerRule: rule })}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                  cfg.blindPartnerRule === rule ? 'bg-navy text-cream' : 'border border-line'
                }`}
              >
                {rule === 'first-tee' ? 'First in tee order' : 'Organizer picks'}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Blind money multiplier</span>
            <Stepper value={cfg.blindMoneyMultiplier ?? 2} onChange={(v) => onChange({ ...cfg, blindMoneyMultiplier: v })} suffix="×" />
          </div>
        </div>
      )}

      {showPointMultipliers && (
        <>
          <div className="text-[10px] font-bold uppercase text-mute">Point multipliers</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <MultRow label="Partner win" value={mult.partnerWin} onChange={(v) => setMult('partnerWin', v)} />
            <MultRow label="Lone win" value={mult.loneWin} onChange={(v) => setMult('loneWin', v)} />
            <MultRow label="Blind win" value={mult.blindWin} onChange={(v) => setMult('blindWin', v)} />
            <MultRow label="Pack vs partner" value={mult.packWinPartner} onChange={(v) => setMult('packWinPartner', v)} />
            <MultRow label="Pack vs lone" value={mult.packWinLone} onChange={(v) => setMult('packWinLone', v)} />
            <MultRow label="Pack vs blind" value={mult.packWinBlind} onChange={(v) => setMult('packWinBlind', v)} />
          </div>
        </>
      )}
    </div>
  );
}

function MultRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-paper px-2 py-1.5">
      <span className="text-ink-soft">{label}</span>
      <Stepper value={value} onChange={onChange} compact />
    </div>
  );
}

function Stepper({ value, onChange, suffix = '', compact }: {
  value: number; onChange: (v: number) => void; suffix?: string; compact?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="h-6 w-6 rounded border border-line text-xs font-bold">−</button>
      <span className={`min-w-6 text-center font-bold ${compact ? 'text-xs' : 'text-sm'}`}>{value}{suffix}</span>
      <button type="button" onClick={() => onChange(value + 1)} className="h-6 w-6 rounded border border-line text-xs font-bold">+</button>
    </div>
  );
}